import React, { useContext } from 'react';
import { AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  const { nightMode } = useContext(AuthContext);
  const navigate = useNavigate();

  return (
    <div className={`min-h-screen ${nightMode ? 'night-gradient text-white' : 'playful-gradient text-gray-900'} page-enter`}>
      <div className="container mx-auto px-6 py-8 max-w-4xl">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)}
          className="mb-6"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>

        <div className={`p-8 rounded-2xl shadow-xl ${nightMode ? 'bg-gray-800/90' : 'bg-white/90 backdrop-blur-sm'}`}>
          <h1 className="text-3xl font-bold mb-6" style={{ fontFamily: 'Fredoka, sans-serif' }}>Privacy Policy</h1>
          
          <div className="prose max-w-none space-y-6">
            <p><strong>Effective Date:</strong> January 1, 2026</p>

            <section>
              <h2 className="text-xl font-bold mb-3">1. Introduction</h2>
              <p>
                Dream Canvas Maker (“we,” “our,” or “us”) values your privacy. This Privacy Policy explains how we collect, use, and protect your information when you use our Android application. Our app is designed to be safe for children under 13 and complies with applicable privacy laws, including COPPA and GDPR.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold mb-3">2. Information We Collect</h2>
              <p>We collect the following information when you use our app:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li><strong>Profile Name</strong> – Used to personalize the app experience.</li>
                <li><strong>Email Address</strong> – Used for account creation, login, and communication.</li>
                <li><strong>Payment Details</strong> – Collected securely for payment processing through Stripe.</li>
                <li><strong>Non-Personal Information</strong> – Such as device type, operating system, and app usage statistics.</li>
              </ul>
              <p className="mt-2">
                We do not collect any other personal information beyond what is necessary for app functionality. This data is never used for advertising or sold to third parties.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold mb-3">3. How We Use Information</h2>
              <p>We use the information we collect to:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Provide and manage our services.</li>
                <li>Process transactions and maintain your account.</li>
                <li>Improve app functionality and user experience.</li>
                <li>Ensure security and compliance with applicable laws.</li>
                <li>Communicate important updates related to your account and our services.</li>
                <li>Analyze usage patterns to enhance performance and reliability.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold mb-3">4. Data Sharing</h2>
              <p>We do not sell or rent personal information. However, we may share information in the following circumstances:</p>
              
              <h3 className="font-bold mt-4 mb-2">Subprocessors</h3>
              <p>We use trusted subprocessors to help deliver our services. These may include:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li><strong>Cloudflare</strong> – For secure edge computing and performance optimization, and AI-driven features.</li>
                <li><strong>Google Gemini</strong> – For AI-driven features and enhancements.</li>
                <li><strong>Stripe</strong> – For payment processing and secure transactions.</li>
                <li><strong>Emergent.sh Cloud</strong> – For cloud hosting, database and infrastructure services.</li>
                <li><strong>DeepL</strong> – For cloud hosting and infrastructure services.</li>                
              </ul>
              <p className="mt-2">We may change subprocessors from time to time and will update this policy accordingly.</p>

              <h3 className="font-bold mt-4 mb-2">Legal Requirements</h3>
              <p>When required by law or to protect our rights.</p>

              <h3 className="font-bold mt-4 mb-2">Business Transfers</h3>
              <p>In connection with a merger, acquisition, or sale of assets.</p>
            </section>

            <section>
              <h2 className="text-xl font-bold mb-3">5. Security</h2>
              <p>We implement industry-standard security measures to protect data, including:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Encryption of sensitive data.</li>
                <li>Secure servers and restricted access.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold mb-3">6. GDPR Rights</h2>
              <p>If you are located in the European Economic Area (EEA), you have the following rights under GDPR:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li><strong>Access:</strong> You can request a copy of the personal data we hold about you.</li>
                <li><strong>Correction:</strong> You can request corrections to inaccurate or incomplete data.</li>
                <li><strong>Deletion (“Right to be Forgotten”):</strong> You can request that we delete your personal data when it is no longer necessary for the purposes for which it was collected, or if you withdraw consent.</li>
                <li><strong>Restriction:</strong> You can request that we limit processing of your data under certain circumstances.</li>
                <li><strong>Data Portability:</strong> You can request a copy of your data in a structured, machine-readable format.</li>
                <li><strong>Objection:</strong> You can object to processing of your data for certain purposes.</li>
              </ul>
              <p className="mt-2">
                To exercise these rights, contact us at support@dreamcanvasmaker.com. We will respond within the timeframes required by law.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold mb-3">7. Third-Party Services</h2>
              <p>
                Our app may use third-party services (e.g., analytics, payment processing) that comply with COPPA, GDPR, and Google Play policies. These services cannot use data for targeted advertising.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold mb-3">8. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. Any changes will be reflected in this document.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold mb-3">9. Contact Us</h2>
              <p>If you have questions about this Privacy Policy or wish to exercise your GDPR rights, contact us at:</p>
              <p className="mt-2">
                <strong>Email:</strong> support@dreamcanvasmaker.com
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;